<template>
  <div>
    <b-modal
      ref="filloutModal"
      content-class="topcard"
      lazy
      hide-footer
      size="xl"
    >
      <template #modal-title>
        Customer Profile
      </template>
      <EditCustomer :customerId="customer.id" @customer-saved="customerSaved" />
    </b-modal>

    <template v-if="customer">
      <b-card class="topcard patient-header">
        <b-row>
          <b-col md="auto">
            <b-row class="h-100">
              <b-col lg="12">
                <div class="d-flex">
                  <h2 @click.prevent="editProfile()" style="cursor: pointer;">
                    {{ customer.firstName }}
                    {{ customer.lastName }}
                    <i class="patient-edit fa-solid fa-pen"></i>
                  </h2>
                </div>
                <div class="status-pill status-pill-id">
                  ID {{ customer.key }}
                </div>
              </b-col>
            </b-row>
          </b-col>
          <b-col style="text-align:right">
            <div style="display: flex;gap:25px; flex-direction: row-reverse;">
              <div lg="auto" v-if="hasRole('BETA')">
                <b-button
                  href="#"
                  class="mt-1 mb-4"
                  variant="primary"
                  @click.prevent="showPurchaseGiftCard()"
                  ><i class="fa-solid fa-gift-card mr-2"></i>Purchase Gift
                  Card</b-button
                >
              </div>
              <div v-if="hasRole('BETA')">
                <b-button
                  href="#"
                  class="mt-1 mb-4"
                  variant="primary"
                  @click.prevent="newInvoice()"
                  ><i class="fa-regular fa-calendar-plus"></i> New
                  Invoice</b-button
                >
              </div>
              <div v-if="hasRole('BETA')">
                <b-button
                  href="#"
                  class="mt-1 mb-4"
                  variant="primary"
                  @click.prevent="addAppt()"
                  ><i class="fa-regular fa-calendar-plus"></i> Schedule
                  Appointment</b-button
                >
              </div>
              <div lg="auto">
                <b-button
                  v-if="state != 'tabs'"
                  href="#"
                  class="mt-1 mb-4"
                  variant="primary"
                  @click.prevent="showMakePayment()"
                  ><i class="fa-regular fa-circle-dollar"></i> Process
                  Payment</b-button
                >
              </div>

              <div v-if="hasRole('BETA')">
                <a href="#" @click.prevent="showCustomerAcctLedger">
                  <div style="font-size:1.2em;font-weight:bold;margin-top:7px;">
                    {{ (customer.balance / 100) | currency }}
                  </div>
                  <span class="patientno mt-3">Balance</span>
                </a>
              </div>
            </div>
          </b-col>
        </b-row>
        <b-row class="m-0">
          <b-col lg="12" class="card shadow-none m-0">
            <b-row align-v="center">
              <b-col class="p-4" md="auto">
                <div class="stat-box-title">Email</div>
                <div class="stat-box-body" style="min-width: 100px;">
                  <span v-if="customer.email">
                    {{ customer.email }}
                  </span>
                  <span v-else @click.prevent="editProfile()">
                    <i
                      class="fa-solid fa-square-plus add-profile add-profile-red"
                      v-b-tooltip.hover
                      title="Required to Recieve Receipts"
                    ></i>
                  </span>
                </div>
              </b-col>
              <b-col>
                <div class="stat-box-title">Mobile</div>
                <div class="stat-box-body">
                  <span v-if="customer.cell">
                    {{ customer.cell | phonenumber }}
                  </span>
                  <span v-else>
                    <i
                      class="fa-solid fa-square-plus add-profile"
                      @click.prevent="editProfile()"
                    ></i>
                  </span>
                </div>
              </b-col>
              <b-col
                class="panel-divider m-0 p-0"
                md="auto"
                v-if="hasRole('BETA')"
              >
                <div @click="showAppts">
                  <stats
                    title="Appointments"
                    class="status-box"
                    :subTitle="customer.apptCount"
                  />
                </div>
              </b-col>
              <b-col
                class="panel-divider m-0 p-0"
                @click="showTransactions()"
                md="auto"
              >
                <stats
                  v-if="showTransaction"
                  title="Transactions"
                  class="status-box"
                  @click="showTransactions()"
                  :subTitle="customer.totalTransactionsCount"
                />
              </b-col>
              <b-col
                class="panel-divider m-0 p-0"
                md="auto"
                v-if="hasRole('VIEWPAYMENTREQUESTS')"
              >
                <stats
                  title="Payment Requests"
                  class="status-box"
                  @clicked="showNotifications()"
                  :subTitle="customer.textToPayTransactionsCount"
                />
              </b-col>
              <b-col
                class="panel-divider m-0 p-0"
                md="auto"
                v-if="hasRole('VIEWPAYMENTPLANS')"
              >
                <stats
                  title="Payment Plans"
                  class="status-box"
                  @clicked="showPaymentPlans"
                  :subTitle="customer.paymentPlanTransactionsCount"
                />
              </b-col>
              <b-col
                class="panel-divider m-0 p-0"
                md="auto"
                v-if="hasRole('VIEWMEMBERSHIPS')"
              >
                <stats
                  title="Memberships"
                  class="status-box"
                  @clicked="showCustomerMembershipBillings()"
                  :subTitle="customer.membershipTransactionsCount"
                />
              </b-col>
            </b-row>
          </b-col>
        </b-row>

        <b-row
          v-if="customer.redeemGiftCardAmount > 0 && hasRole('BETA')"
          class="m-0 mb-2 mt-4 light-alert light-alert-error"
          align-v="center"
        >
          <b-col class="p-0">
            <i class="fa-solid fa-bell-on"></i>This customer has gift cards
            awaiting to be redeemed in the amount of of
            <strong>
              {{ (customer.redeemGiftCardAmount / 100) | currency }}</strong
            >.
          </b-col>
          <b-col class="p-0 text-right">
            <b-button variant="danger" @click.prevent="showRedeem()" href=""
              >View & Redeem Gift Card</b-button
            >
          </b-col>
        </b-row>
      </b-card>

      <!-- Payment States --->

      <div v-if="state == 'newappt'">
        <NewAppt :customer="customer" v-if="customer" />
      </div>

      <div v-if="state == 'newinvoice'">
        <Invoice :customer="customer" />
      </div>

      <!-- Detail States --->

      <b-card
        body-class="p-0"
        header-class="border-0"
        v-if="state == 'acctLedger'"
      >
        <AcctLedger :customer="customer" />
      </b-card>

      <div v-if="state == 'transactions'">
        <b-card body-class="p-0" header-class="border-0">
          <transaction-search-results
            supressPatientName="true"
            :query="getTransactionQuery()"
          ></transaction-search-results>
        </b-card>
      </div>

      <div v-if="state == 'notifications'">
        <b-card body-class="p-0" header-class="border-0">
          <notification-search-results
            supressPatientName="true"
            :query="getNotificationsQuery()"
          ></notification-search-results>
        </b-card>
      </div>

      <div v-if="state == 'paymentplans'">
        <b-card body-class="p-0" header-class="border-0">
          <paymentPlan-search-results
            supressPatientName="true"
            :query="getPaymentPlanQuery()"
          ></paymentPlan-search-results>
        </b-card>
      </div>

      <div v-if="state == 'customerMembershipBillings'">
        <b-card body-class="p-0" header-class="border-0">
          <customerMembershipBilling-search-results
            supressPatientName="true"
            :query="getCustomerMembershipBillingQuery()"
          ></customerMembershipBilling-search-results>
        </b-card>
      </div>

      <div v-if="state == 'appts'">
        <b-card body-class="p-0" header-class="border-0">
          <appt-search-results
            supressPatientName="true"
            :query="getApptsQuery()"
          ></appt-search-results>
        </b-card>
      </div>

      <div v-if="state == 'purchaseGiftCard'">
        <purchase-gift-card :customer="customer" />
      </div>

      <div v-if="state == 'redeemGiftCard'">
        <b-card body-class="p-0" header-class="border-0">
          <RedeemGiftCard
            :customer="customer"
            @refreshCustomer="refreshCustomer"
          />
        </b-card>
      </div>

      <!-- Payment Tabs --->

      <div class="mt--2" v-if="state == 'tabs'">
        <b-card no-body>
          <b-tabs card v-model="tabIndex" v-on:activate-tab="checkEmail">
            <b-tab
              title="Terminal Payment"
              lazy
              v-if="hasRole('PROCESSTERMINALPAYMENTS')"
            >
              <b-card-text>
                <terminal
                  :customer="customer"
                  :afterTransactionUri="afterTransactionUri"
                  :amount="amount"
                />
              </b-card-text>
            </b-tab>
            <b-tab
              title="Virtual Payment"
              lazy
              v-if="hasRole('PROCESSVIRTUALPAYMENTS')"
            >
              <b-card-text
                ><virtual
                  :customer="customer"
                  :afterTransactionUri="afterTransactionUri"
              /></b-card-text>
            </b-tab>
            <b-tab
              title="Payment Plan"
              lazy
              v-if="hasRole('CREATEPAYMENTPLANS')"
            >
              <b-card-text
                ><paymentPlan
                  :customer="customer"
                  v-if="customer"
                  @editPatient="editPatient"
              /></b-card-text>
            </b-tab>
            <b-tab title="Membership" lazy v-if="hasRole('CREATEMEMBERSHIPS')">
              <b-card-text
                ><ongoing :customer="customer" v-if="customer"
              /></b-card-text>
            </b-tab>
            <b-tab
              title="Payment Request"
              lazy
              v-if="hasRole('CREATEPAYMENTREQUESTS')"
            >
              <b-card-text
                ><textToPay :customer="customer" v-if="customer"
              /></b-card-text>
            </b-tab>
          </b-tabs>
        </b-card>
      </div>
    </template>
  </div>
</template>
<script>
import PaymentPlan from "./Actions/PaymentPlan";
import OnGoing from "./Actions/OnGoing";
import TextToPay from "./Actions/TextToPay";
import PayByTerminal from "./Actions/PayByTerminal";
import PayByVirtual from "./Actions/PayByVirtual";

import NewAppt from "./Actions/NewAppt.vue";
import TransactionSearchResults from "@/views/Components/Transactions/SearchResults";
import PaymentPlanSearchResults from "@/views/Components/PaymentPlans/SearchResults";
import NotificationSearchResults from "@/views/Components/Notifications/SearchResults";
import CustomerMembershipBillingSearchResults from "@/views/Components/CustomerMembershipBilling/SearchResults";
import TextToPayNotification from "@/views/Components/Notifications/TextToPay";
import swal from "sweetalert2";
import Stats from "@/components/Cards/Stats.vue";
import EditCustomer from "@/views/Components/Customer/EditCustomer.vue";
import AcctLedger from "./AcctLedger.vue";
import PurchaseGiftCard from "./Actions/PurchaseGiftCard.vue";
import ApptSeachResults from "@/views/Components/Appt/SearchResults.vue";
import RedeemGiftCard from "./RedeemGiftCard.vue";
import Invoice from "@/views/Components/Invoice/Invoice.vue";
export default {
  name: "profile-header",
  components: {
    RedeemGiftCard,
    PurchaseGiftCard,
    paymentPlan: PaymentPlan,
    ongoing: OnGoing,
    textToPay: TextToPay,
    terminal: PayByTerminal,
    virtual: PayByVirtual,
    TransactionSearchResults,
    PaymentPlanSearchResults,
    TextToPayNotification,
    NotificationSearchResults,
    CustomerMembershipBillingSearchResults,
    NewAppt,
    EditCustomer,
    Invoice,
    Stats,

    AcctLedger,
    "appt-search-results": ApptSeachResults,
  },
  inject: ["systemContextService"],
  props: {},
  inject: ["hasRole"],
  data() {
    return {
      showPaymentOptionButton: false,
      current: { state: "tabs" },
      tabIndex: 0,
      customer: {},

      amount: null,
      state: null,
    };
  },
  props: {
    showPaymentPlan: {
      type: Boolean,
      default: true, // default|light
      description: "Show payment plan options",
    },
    showMembership: {
      type: Boolean,
      default: true, // default|light
      description: "Show membership options",
    },
    showPayByText: {
      type: Boolean,
      default: true, // default|light
      description: "Show payment request options",
    },
    showTransaction: {
      type: Boolean,
      default: true, // default|light
      description: "Show payment request options",
    },
    afterTransactionUri: {
      type: String,
      default: "/#/dashboard", // default|light
      description: "Where do we go after the transaction has processed",
    },
  },
  computed: {
    q() {
      let o = `${this.$route.params.id}|${this.$route.query.editCustomer}`;

      return o;
    },
  },
  watch: {
    q: {
      handler: function(newVal, oldVal) {
        const [oldPropertyA, oldProvertyB] = oldVal.split("|");
        const [newPropertyA, newProvertyB] = newVal.split("|");

        if (oldPropertyA != newPropertyA) {
          this.state = "tabs";
          this.tabIndex = 0;
          this.bind();
        }

        if (newProvertyB == "true") this.editProfile();
      },
      deep: true,
      immediate: true,
    },
  },
  async mounted() {
    var self = this;

    this.state = "tabs";
    this.bind();
  },
  methods: {
    refreshCustomer() {
      this.state = null;
      this.bind();
    },
    customerSaved(customer) {
      this.customer.firstName = customer.firstName;
      this.customer.lastName = customer.lastName;
      this.customer.email = customer.email;
      this.customer.cell = customer.cell;

      this.$refs.filloutModal.hide();
    },
    newInvoice() {
      this.state = "newinvoice";
    },
    addAppt() {
      this.state = "newappt";
    },
    editPatient() {
      this.editProfile();
    },
    checkEmail(newIndex, oldIndex, event) {
      if (!this.customer.email && (newIndex == 2 || newIndex == 3)) {
        event.preventDefault();
        swal
          .fire({
            title: "Email Required",
            text: "Please add the patients email address before proceeding.",
            buttonsStyling: false,
            confirmButtonClass: "btn btn-warning",
            icon: "warning",
          })
          .then((r) => {
            this.editProfile();
          });
      }
    },
    showCustomerAcctLedger() {
      this.state = "acctLedger";
    },
    showRedeem() {
      this.state = "redeemGiftCard";
    },

    showAppts() {
      this.state = "appts";
    },
    showPurchaseGiftCard() {
      this.state = "purchaseGiftCard";
    },
    sendNewText() {
      var self = this;
      self.showPaymentOptionButton = false;
      self.state = "tabs";
      self.tabIndex = 4;
    },
    showNotifications() {
      var self = this;
      self.showPaymentOptionButton = true;
      self.state = "notifications";
    },
    showPaymentPlans() {
      var self = this;
      self.showPaymentOptionButton = true;
      self.state = "paymentplans";
    },
    showMakePayment() {
      var self = this;
      self.showPaymentOptionButton = false;
      self.state = "tabs";
      self.tabIndex = 0;
    },
    showTransactions() {
      var self = this;
      self.showPaymentOptionButton = true;
      self.state = "transactions";
    },
    showCustomerMembershipBillings() {
      var self = this;
      self.showPaymentOptionButton = true;
      self.state = "customerMembershipBillings";
    },
    getNotificationsQuery() {
      var self = this;
      return {
        customerId: self.$route.params.id,
      };
    },
    getTransactionQuery() {
      var self = this;
      return {
        customerId: self.$route.params.id,
        statuses: ["APPROVED", "DECLINED"],
        currentPage: 1,
        modules: [
          "TEXTTOPAY",
          "PAYMENTPLAN",
          "MEMBERSHIP",
          "TERMINAL",
          "PAYMENTPAGE",
          "VIRTUALTERMINAL",
        ],
        sortOption: "Date:descending",
      };
    },
    getPaymentPlanQuery() {
      var self = this;
      return {
        customerId: self.$route.params.id,
        statuses: ["ACTIVE", "COMPLETE", "INACTIVE"],
        currentPage: 1,
        sortOption: "Date:descending",
      };
    },
    getCustomerMembershipBillingQuery() {
      return {
        customerId: this.$route.params.id,
        status: ["ACTIVE", "INACTIVE", "AWAITINGPATIENT"],
      };
    },
    getApptsQuery() {
      return {
        customerId: this.$route.params.id,
      };
    },
    async bind() {
      var self = this;

      self.customer = null;

      var r = await self.$client.get(
        "/api/customer/getCustomer?id=" + self.$route.params.id
      );

      if (r.data.isSuccess) {
        self.customer = r.data.value;

        if (r.data.value.balance > 0) this.amount = r.data.value.balance;

        document.title = self.customer.firstName + " " + self.customer.lastName;

        if (r.data.value.hasUserNotifications) {
          this.$emitter.emit(
            "show-userNotification",
            r.data.value.hasUserNotifications
          );
        } else {
          this.$emitter.emit(
            "hide-userNotification",
            r.data.value.hasUserNotifications
          );
        }
      }
    },
    load(comp) {
      this.componentRef = comp;
    },

    editProfile() {
      this.$refs.filloutModal.show();
    },
  },
};
</script>

<style>
.profile-icon {
  color: #adb5bd;
  top: 1px;
  padding-right: 5px;
  position: relative;
}

.patient-edit {
  font-size: 0.6em;
  color: #adb5bd;
  position: relative;
  padding-left: 5px;
  top: -3px;
}

.topcard h2 {
  display: inline-block;
  clear: both;
  overflow: hidden;
}

.topcard h2 .patient-edit {
  animation: fadeIn 1s;
  -webkit-animation: fadeIn 1s;
  -moz-animation: fadeIn 1s;
  -o-animation: fadeIn 1s;
  -ms-animation: fadeIn 1s;
}

.topcard h2:hover .patient-edit {
  display: inline-block;
  color: #007cbe;
}

.stat-box-title {
  font-size: 0.8em;
  font-weight: 600;
  color: #8898aa;
}

.box-icon {
  font-size: 0.9em;
  padding-right: 5px;
}

.box-trans .box-icon {
  color: #8acbcc;
}

.box-text .box-icon {
  color: #f9d989;
}

.box-plans .box-icon {
  color: #79b3ea;
}

.box-memberships .box-icon {
  color: #f08a9e;
}

.text-warning .fa-circle-envelope {
  font-size: 1.6em;
}

.stats-box {
  min-width: 150px;
}

.patient-contact .fa-solid {
  color: #adb5bd;
  font-size: 1.2em;
}

.add-profile {
  color: #ced4da;
}

.add-profile:hover {
  color: #4693ce;
  cursor: pointer;
}

.add-profile-red,
.add-profile-red:hover {
  color: #fb6340;
}

.status-box {
  height: 100%;
  padding: 1.4rem;
  min-width: 160px;
  text-align: right;
}

.patient-header .showchart {
  display: none;
}

.patient-header .status-box:hover .data-data,
.patient-header .status-box:hover .data-head {
  color: #4693ce !important;
}

@keyframes fadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@-moz-keyframes fadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@-webkit-keyframes fadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@-o-keyframes fadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@-ms-keyframes fadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.modal .card-header {
  font-weight: 600;
  background: #fff;
}

.note-body {
  background: #e9ecef;
  border-radius: 4px;
  padding: 15px;
}

.note-footer {
  font-size: 0.8em;
  padding: 10px 10px 20px 10px;
}

.note:last-child .note-footer {
  padding-bottom: 0px;
}

.triangle {
  margin-left: 20px;
  width: 0;
  border-top: 15px solid #e9ecef;
  border-left: 15px solid transparent;
  border-right: 15px solid transparent;
  cursor: pointer;
}
</style>
